// frontend/src/core/modules/RequireModule.tsx
//
// Layout-route guard: if the tenant has the named module, render the nested
// routes (<Outlet/>); otherwise show an upsell card pointing at the billing
// page. Server-side requireModule() is still the real gate — this is UX so
// users see "upgrade" instead of a raw 402/403.

import * as React from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { useEnabledModules } from './EnabledModulesProvider';

const MODULE_LABEL: Record<string, string> = {
  invoicing: 'Invoicing',
  hr: 'HR',
  payroll: 'Payroll',
  payments: 'Payments',
  projects: 'Projects',
  monitoring: 'Monitoring',
  email: 'Email',
  expenses: 'Expenses',
  esign: 'E-Sign',
};

export function RequireModule({ module }: { module: string }) {
  const { modules, loaded } = useEnabledModules();

  // Don't render anything (or the upsell) until we know — avoids a flash.
  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-32 text-ink/40 dark:text-white/40 text-[13px]">
        Loading…
      </div>
    );
  }

  if (modules.has(module)) return <Outlet />;

  const label = MODULE_LABEL[module] ?? module;
  return (
    <div className="px-6 sm:px-8 lg:px-10 py-16">
      <div className="max-w-md mx-auto text-center rounded-3xl bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card p-10">
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-coral/10 text-coral mb-5">
          <Lock className="h-6 w-6" />
        </div>
        <h1 className="text-xl font-light text-ink dark:text-white">{label} isn't in your plan</h1>
        <p className="mt-2 text-[14px] text-ink/60 dark:text-white/60">
          The {label} module isn't enabled for your workspace. Add it from your subscription to
          start using it.
        </p>
        <Link
          to="/dashboard/billing"
          className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-ink-900 dark:bg-coral text-white px-5 py-2.5 text-[13px] font-semibold hover:opacity-90 transition-opacity"
        >
          Manage subscription
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
